import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { highlevelOpportunitiesService } from "../../services/highlevelOpportunitiesService";
import { MoreHorizontal, Search, AlertCircle } from "lucide-react";

export default function Pipeline() {
  const { vertical, config, branchFilter } = useOutletContext();
  const navigate = useNavigate();
  const [leads, setLeads] = useState(config.leads);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLeads(config.leads);
    setSearch("");
  }, [config]);

  const filtered = useMemo(() => {
    let rows = leads;
    if (branchFilter !== "all") rows = rows.filter((l) => l.branch_name === branchFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((l) => l.parent_name.toLowerCase().includes(q) || (l.child_name || "").toLowerCase().includes(q) || l.programme_name.toLowerCase().includes(q));
    }
    return rows;
  }, [leads, branchFilter, search]);

  const moveStage = async (lead, stage) => {
    setLeads((prev) => prev.map((l) => (l.contact_id === lead.contact_id ? { ...l, stage: stage.id, stage_name: stage.name } : l)));
    if (vertical === "enrichment") {
      await highlevelOpportunitiesService.moveStage(lead.contact_id, stage.id);
    }
    toast.success(`${lead.parent_name} moved to “${stage.name}”`);
  };

  const timeAgo = (iso) => {
    const hrs = Math.max(1, Math.round((Date.now() - new Date(iso)) / 36e5));
    return hrs < 24 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
  };

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${config.entityLabel.toLowerCase()}s…`} className="h-10 rounded-xl pl-9 bg-white" data-testid="pipeline-search-input" />
        </div>
        <p className="text-xs text-muted-foreground">{filtered.length} {config.entityLabel.toLowerCase()}s · {config.stages.length} stages · click a card for detail</p>
      </div>

      <div className="overflow-x-auto scrollbar-thin pb-3 -mx-4 px-4 sm:-mx-6 sm:px-6" data-testid="pipeline-kanban-board">
        <div className="flex gap-3" style={{ minWidth: "max-content" }}>
          {config.stages.map((stage) => {
            const cards = filtered.filter((l) => l.stage === stage.id);
            const hasSla = cards.some((c) => c.first_response_mins > 60 && ["new_enquiry", "contacted", "new_lead", "recall_due"].includes(c.stage));
            return (
              <div key={stage.id} className="w-[248px] shrink-0" data-testid={`pipeline-kanban-column-${stage.id}`}>
                <div className="flex items-center justify-between px-1.5 mb-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {hasSla && <AlertCircle className="h-3 w-3 text-rose-500 shrink-0" />}
                    <p className="text-xs font-semibold text-foreground truncate">{stage.name}</p>
                  </div>
                  <Badge variant="secondary" className="text-[10px] tabular-nums shrink-0">{cards.length}</Badge>
                </div>
                <div className="bg-[hsl(210_24%_93%)] rounded-2xl p-2 space-y-2 min-h-[120px] max-h-[62vh] overflow-y-auto scrollbar-thin">
                  {cards.length === 0 && <p className="text-[11px] text-muted-foreground text-center py-8">No {config.entityLabel.toLowerCase()}s</p>}
                  {cards.slice(0, 30).map((lead) => (
                    <div
                      key={lead.contact_id}
                      className="bg-white border border-border rounded-xl p-3 shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow cursor-pointer group"
                      onClick={() => navigate(`/demo/leads/${lead.contact_id}`)}
                      data-testid={`pipeline-kanban-card-${lead.contact_id}`}
                    >
                      <div className="flex items-start justify-between gap-1">
                        <p className="text-sm font-semibold text-foreground leading-tight">{lead.parent_name}</p>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-6 w-6 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shrink-0" data-testid={`pipeline-card-menu-${lead.contact_id}`}>
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white max-h-72 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuLabel className="text-xs">Move to…</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {config.stages.filter((s) => s.id !== lead.stage).map((s) => (
                              <DropdownMenuItem key={s.id} className="text-xs" onClick={() => moveStage(lead, s)}>{s.name}</DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {lead.child_name && <p className="text-xs text-muted-foreground mt-0.5">{lead.child_name}{lead.child_level ? ` · ${lead.child_level}` : ""}</p>}
                      <div className="flex flex-wrap gap-1 mt-2">
                        <Badge variant="secondary" className="text-[9px] px-1.5">{lead.programme_name}</Badge>
                        <Badge variant="secondary" className="text-[9px] px-1.5 bg-[hsl(199_78%_92%)] text-[hsl(215_84%_22%)]">{lead.branch_name}</Badge>
                      </div>
                      <div className="flex items-center justify-between mt-2.5">
                        <p className="text-[10px] text-muted-foreground truncate">{lead.next_action}</p>
                        <span className="text-[10px] text-muted-foreground shrink-0 ml-1.5 tabular-nums">{timeAgo(lead.last_activity_date)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
